import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Box, SxProps, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { UserRolesEnum } from '../../../services/entities/impl/blfApi/schemas/user';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import IconButtonProtected from '../../commonUI/IconButtonProtected';
import { DiagnosticFeature } from '../../commonUI/viewMode/DiagnosticFeature';
import { DebugInfoLauncherOnboarding } from '../../debugInfo/onboarding/DebugInfoLauncherOnboarding';
import {
  OnboardFormStepEnum,
  OnboardingTabsEnum,
  selectCurrOnboardingTab,
  setCurrOnboardBlockID,
  setCurrOnboardCustomerID,
  setCurrOnboardRanchID,
  setCurrOnboardUserID,
  setOnboardFormStep,
} from '../onboardingSlice';
import { isUserRole } from '../onboardingUtils';
const commonStyles: SxProps = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
};

export interface OnboardSubtitle {
  title: string;
  text: string | null | undefined;
  hidden?: boolean;
  hasError?: boolean;
}

export interface ItemIDs {
  customerID: string;
  ranchID: string;
  blockID: string;
  userID: string;
}

export interface OnboardListItemParams {
  id: string;
  title: string;
  itemIDs: ItemIDs;
  subTitles?: OnboardSubtitle[];
  warnings?: string[];
  errors?: string[];
}

interface Props {
  onboardListItemParams: OnboardListItemParams;
}

const OnboardListItem: React.FC<Props> = ({ onboardListItemParams }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currTab = useAppSelector(selectCurrOnboardingTab);

  const {
    title,
    itemIDs,
    subTitles = [],
    warnings = [],
    errors = [],
  } = onboardListItemParams;

  const commonAlertStyles: SxProps = {
    width: '100%',
    pt: 0,
    pb: 0,
    borderRadius: theme.borderRadius.primary,
  };

  // Set the form step and select the item in the select dropdowns.  This sets the id's in the local onboard slice
  const handleFormActionClick = (
    e: React.MouseEvent<HTMLElement>,
    formStep: OnboardFormStepEnum
  ) => {
    e.stopPropagation();
    dispatch(setCurrOnboardCustomerID(itemIDs.customerID));
    dispatch(setCurrOnboardRanchID(itemIDs.ranchID));
    dispatch(setCurrOnboardBlockID(itemIDs.blockID));
    dispatch(setCurrOnboardUserID(itemIDs.userID));
    dispatch(setOnboardFormStep(formStep));
  };

  const renderedSubTitles = subTitles.map((row, idx) => {
    if (row.hidden) return null;

    return (
      <Box
        key={idx}
        sx={{
          width: '100%',
          maxWidth: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          flexDirection: 'row',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <Typography
          variant='subtitle1'
          sx={{
            // Color user roles differently than normal titles
            color:
              currTab === OnboardingTabsEnum.users && isUserRole(row.title)
                ? theme.colors.darkTheme.purple
                : theme.colors.darkTheme.blue,
            fontWeight: 'bold',
          }}
        >
          {row.title}
        </Typography>
        <Typography
          variant='subtitle1'
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ml: '0.2rem',
            color:
              row.text && !row.hasError
                ? theme.colors.common.white
                : theme.colors.darkTheme.red,
          }}
        >{`- ${
          row.text ? row.text : t('Missing', { ns: 'common' })
        }`}</Typography>
      </Box>
    );
  });

  const renderedWarnings = warnings.map((warning, idx) => (
    <Alert
      key={idx}
      severity='warning'
      variant='filled'
      sx={{ ...commonAlertStyles }}
    >
      {t(warning, { ns: 'common' })}
    </Alert>
  ));

  const renderedErrors = errors.map((error, idx) => (
    <Alert
      key={idx}
      severity='error'
      variant='filled'
      sx={{ ...commonAlertStyles }}
    >
      {error}
    </Alert>
  ));

  return (
    <Box sx={{ width: '100%', mb: '0.8rem' }}>
      <Box
        sx={{
          width: '100%',
          transition: 'all 0.25s ease-out',
          boxShadow:
            '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
          '&:hover': {
            cursor: 'pointer',
            transform: 'translateY(-3px)',
            boxShadow:
              '0px 8px 4px -4px rgb(0 0 0 / 20%), 0px 4px 4px 0px rgb(0 0 0 / 14%), 0px 4px 12px 0px rgb(0 0 0 / 12%)',
          },
          borderRadius: theme.recentScansPage.card.borderRadius,
          position: 'relative',
          bgcolor: theme.colors.darkTheme.grey.dark,
          p: '1rem',
        }}
        onClick={(e) => handleFormActionClick(e, OnboardFormStepEnum.View)}
      >
        <Box
          sx={{
            ...commonStyles,
            flexDirection: 'row',
          }}
        >
          <Box
            sx={{
              ...commonStyles,
              maxWidth: '100%',
              overflow: 'hidden',
              alignItems: 'flex-start',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: (theme) =>
                  `1px solid ${theme.colors.darkTheme.grey.muted}`,
                mb: '0.5rem',
              }}
            >
              <Typography
                variant='h6'
                sx={{
                  maxWidth: '100%',
                  color: theme.colors.common.bloomfieldGreen,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {title}
              </Typography>
              <Box
                sx={{
                  width: 'min-content',
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                }}
              >
                <DiagnosticFeature>
                  <DebugInfoLauncherOnboarding
                    customerID={itemIDs.customerID}
                    ranchID={itemIDs.ranchID}
                    blockID={itemIDs.blockID}
                    debugInfoUserID={itemIDs.userID}
                  />
                </DiagnosticFeature>
                <IconButtonProtected
                  icon={<EditIcon />}
                  handleClick={(e) =>
                    handleFormActionClick(e, OnboardFormStepEnum.Edit)
                  }
                  authorizedRoles={[UserRolesEnum.root]}
                  sx={{ color: theme.colors.common.white }}
                />
                <IconButtonProtected
                  icon={<DeleteIcon />}
                  handleClick={(e) =>
                    handleFormActionClick(e, OnboardFormStepEnum.Delete)
                  }
                  authorizedRoles={[UserRolesEnum.root]}
                  sx={{ color: theme.colors.common.white }}
                />
              </Box>
            </Box>
            {/* <Box
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '0 0.5rem',
              }}
            > */}
            {renderedSubTitles}
            {/* </Box> */}
          </Box>
        </Box>
        {(renderedWarnings.length > 0 || renderedErrors.length > 0) && (
          <Box
            sx={{
              width: '100%',
              // ...flexCenter,
              // flexDirection: 'column',
              mt: '0.6rem',
              gap: '0.5rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            }}
          >
            {renderedWarnings}
            {renderedErrors}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OnboardListItem;
